/**
 * features/finance/RecurringVouchers.tsx
 * ------------------------------------------------------------------
 * THE BILLS THAT ARRIVE EVERY MONTH.
 *
 * Security, rent, cleaning, insurance, the internet line. Same payee,
 * same amount, every period. Retyping them is where the forgotten
 * payment comes from, and a forgotten payment is a supplier ringing the
 * school about an invoice it has had for six weeks.
 *
 * A TEMPLATE NEVER PAYS ANYTHING. Generating produces a DRAFT voucher
 * which then walks prepare → check → approve like every other. The whole
 * control of a voucher is three distinct people signing it, and a
 * schedule that could approve its own payments would remove that quietly
 * and completely. This is a typing aid with a memory, and the screen
 * says so where somebody about to press the button will read it.
 *
 * IT CATCHES UP RATHER THAN SKIPPING. A template left ungenerated for
 * three months offers all three, each dated to its own period. Generating
 * only the current one silently loses the two the school still owes.
 *
 * READ BEFORE POSTING. Everything about to be created is listed first.
 * These are entries nobody typed, so nobody reads them afterwards either
 * — this is the only moment somebody looks.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query } from 'firebase/firestore';
import {
  Repeat, Plus, Play, X, AlertTriangle, CheckCircle2, Pause, Info,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { db, setDoc, updateDoc } from '@/lib/firebase';
import {
  RECURRING_VOUCHER_COLLECTION, recurringVouchersDue, summariseRecurring,
  recurringVoucherProblem,
  type RecurringVoucher,
} from '@/lib/recurringVouchers';
import {
  VOUCHER_COLLECTIONS, type Voucher, type VoucherLine,
} from '@/lib/vouchers';
import { createVoucher } from '@/features/finance/voucherWrite';
import { useGlData } from '@/features/finance/useGlData';
import { SUPPLIER_COLLECTION, type Supplier } from '@/lib/suppliers';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
  modalBackdrop, modalSurface,
} from '@/features/finance/ui';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const thisPeriod = () => new Date().toISOString().slice(0, 7);

export default function RecurringVouchers() {
  const { userData } = useAuth();
  const gl = useGlData(`FY${new Date().getFullYear()}`);

  const [templates, setTemplates] = useState<RecurringVoucher[] | null>(null);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ made: number; failed: string[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => onSnapshot(query(collection(db, RECURRING_VOUCHER_COLLECTION)),
    (s) => setTemplates(s.docs.map((d) => ({ id: d.id, ...d.data() }) as RecurringVoucher)),
    () => setTemplates([])), []);
  useEffect(() => onSnapshot(query(collection(db, VOUCHER_COLLECTIONS.vouchers)),
    (s) => setVouchers(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Voucher)),
    () => setVouchers([])), []);
  useEffect(() => onSnapshot(query(collection(db, SUPPLIER_COLLECTION)),
    (s) => setSuppliers(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Supplier)),
    () => setSuppliers([])), []);

  /* What has already been generated, read off the vouchers themselves.
     A separate "last generated" field on the template would be a second
     record of the same fact, and the two would eventually disagree. */
  const generated = useMemo(
    () => new Set(vouchers.map((v) => v.source?.id).filter((x): x is string => Boolean(x))),
    [vouchers],
  );

  const due = useMemo(
    () => recurringVouchersDue(templates ?? [], generated, thisPeriod()),
    [templates, generated],
  );

  const summary = useMemo(
    () => summariseRecurring(templates ?? [], due, thisPeriod()),
    [templates, due],
  );

  async function generateAll() {
    setBusy(true);
    setResult(null);
    setErr(null);
    const failed: string[] = [];
    let made = 0;

    for (const d of due) {
      try {
        await createVoucher({
          fy: `FY${d.period.slice(0, 4)}`,
          source: { type: 'recurring', id: d.sourceId },
          payeeName: d.template.payeeName,
          supplierId: d.template.supplierId,
          lines: d.template.lines,
          narration: `${d.template.narration || d.template.name} — ${d.period}`,
          dueDate: d.date,
          paymentRef: d.template.paymentRef,
          method: d.template.method,
        }, {
          uid: userData?.uid ?? '',
          displayName: userData?.displayName ?? '',
          email: userData?.email ?? '',
        });
        made += 1;
      } catch (e) {
        failed.push(`${d.template.name} ${d.period}: ${(e as Error).message}`);
      }
    }

    setResult({ made, failed });
    setBusy(false);
  }

  async function toggle(t: RecurringVoucher) {
    try {
      await updateDoc(doc(db, RECURRING_VOUCHER_COLLECTION, t.id), {
        active: !t.active, updatedAt: new Date().toISOString(),
      });
    } catch (e) { setErr((e as Error).message); }
  }

  if (templates === null || gl.loading) {
    return <div className="p-6 flex justify-center"><Spinner /></div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <PageHeader
        icon={Repeat} tone="navy"
        title="Standing Bills"
        subtitle="The same payee every month — typed once, remembered after that"
        actions={(
          <button type="button" className={btnPrimary} onClick={() => setAdding(true)}>
            <Plus size={15} /> New standing bill
          </button>
        )}
      />

      {err && (
        <SoftCard className="mb-4 border-l-4 border-red-400">
          <p className="text-sm text-red-700 dark:text-red-300">{err}</p>
        </SoftCard>
      )}

      {result && (
        <SoftCard className={`mb-4 border-l-4 ${result.failed.length ? 'border-amber-400' : 'border-emerald-400'}`}>
          <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
            {result.failed.length
              ? <AlertTriangle size={15} className="text-amber-600" />
              : <CheckCircle2 size={15} className="text-emerald-600" />}
            {result.made} draft voucher{result.made === 1 ? '' : 's'} created
            {result.failed.length ? `, ${result.failed.length} refused` : ''}.
          </p>
          <p className="text-[12px] text-gray-600 dark:text-gray-300 mt-0.5">
            They are DRAFTS. Each still has to be checked and approved by different people
            before anything is paid.
          </p>
          {result.failed.map((f) => (
            <p key={f} className="text-[12px] text-gray-600 dark:text-gray-300">· {f}</p>
          ))}
        </SoftCard>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
        <StatChip tone="navy" icon={Repeat} value={String(summary.active)} label="Standing bills" />
        <StatChip tone="slate" value={money(summary.monthlyCommitment)} label="Committed each month" />
        <StatChip tone={summary.due ? 'amber' : 'emerald'}
          value={String(summary.due)} label="Waiting to be raised" />
        <StatChip tone={summary.worstBacklog > 1 ? 'rose' : 'slate'}
          value={summary.worstBacklog ? `${summary.worstBacklog}m` : '—'} label="Furthest behind" />
      </div>

      {due.length > 0 && (
        <SoftCard pad="p-0" className="mb-5">
          <PanelHeader
            icon={Play} tone="navy" title="About to be raised"
            subtitle="Read it first — nobody typed these, so nobody reads them afterwards either"
            right={(
              <button type="button" className={btnPrimary} onClick={generateAll} disabled={busy}>
                {busy ? 'Creating…' : `Create ${due.length} draft${due.length === 1 ? '' : 's'}`}
              </button>
            )}
          />
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                <th className="py-2 pl-4 pr-2 font-semibold">Bill</th>
                <th className="px-2 font-semibold">Payee</th>
                <th className="px-2 font-semibold">Period</th>
                <th className="px-2 font-semibold">Dated</th>
                <th className="px-2 pr-4 font-semibold text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {due.map((d) => (
                <tr key={d.sourceId} className="border-b border-gray-50 dark:border-gray-800">
                  <td className="py-2 pl-4 pr-2 text-gray-900 dark:text-white">
                    {d.template.name}
                  </td>
                  <td className="px-2 text-gray-600 dark:text-gray-300">{d.template.payeeName}</td>
                  <td className="px-2 tabular-nums text-gray-500">{d.period}</td>
                  <td className="px-2 tabular-nums text-gray-500">{d.date}</td>
                  <td className="px-2 pr-4 text-right tabular-nums">{money(d.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-[12px] text-gray-500 flex items-start gap-1.5">
            <Info size={13} className="mt-0.5 shrink-0" />
            Each is dated in its OWN period, not today — a March bill raised in June belongs
            to March, and dating it today would put three months of cost in one month.
          </p>
        </SoftCard>
      )}

      <SoftCard pad="p-0">
        <PanelHeader icon={Repeat} tone="navy" title="Standing bills"
          subtitle="Stopped, never deleted — what a template raised still stands" />
        {templates.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">
            None yet. A bill that arrives every month is worth typing once.
          </p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {templates.map((t) => {
                const problem = recurringVoucherProblem(t);
                const total = (t.lines ?? []).reduce((n, l) => n + (Number(l.amount) || 0), 0);
                return (
                  <tr key={t.id} className={`border-b border-gray-50 dark:border-gray-800 ${
                    t.active ? '' : 'opacity-50'
                  }`}>
                    <td className="py-2.5 pl-4 pr-2">
                      <span className="font-medium text-gray-900 dark:text-white">{t.name}</span>
                      <span className="block text-[11px] text-gray-400">
                        {t.payeeName} · from {t.startPeriod}
                        {t.endPeriod ? ` to ${t.endPeriod}` : ''}
                        {t.dueDay ? ` · due the ${t.dueDay}th` : ''}
                      </span>
                      {problem && (
                        <span className="block text-[11px] text-amber-700 dark:text-amber-300">
                          {problem} — nothing will be raised until this is fixed.
                        </span>
                      )}
                    </td>
                    <td className="px-2 text-right tabular-nums">{money(total)}</td>
                    <td className="px-2 pr-4 text-right">
                      <button type="button" className={btnGhost} onClick={() => toggle(t)}>
                        {t.active ? <><Pause size={13} /> Stop</> : <><Play size={13} /> Start</>}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </SoftCard>

      {adding && (
        <NewTemplate
          accounts={gl.accounts} suppliers={suppliers}
          actor={{ uid: userData?.uid ?? '' }}
          onClose={() => setAdding(false)} onError={setErr}
        />
      )}
    </div>
  );
}

function NewTemplate({
  accounts, suppliers, actor, onClose, onError,
}: {
  accounts: ReturnType<typeof useGlData>['accounts'];
  suppliers: Supplier[];
  actor: { uid: string };
  onClose: () => void;
  onError: (m: string) => void;
}) {
  const [f, setF] = useState<Partial<RecurringVoucher>>({
    startPeriod: thisPeriod(), dueDay: 1, method: 'eft', active: true,
    lines: [{ description: '', accountCode: '', amount: 0 }],
  });
  const [busy, setBusy] = useState(false);

  const problem = recurringVoucherProblem(f as RecurringVoucher);
  const expenses = accounts.filter((a) => a.type === 'expense' && a.isPostable !== false);

  const setLine = (i: number, patch: Partial<VoucherLine>) =>
    setF({ ...f, lines: (f.lines ?? []).map((l, idx) => (idx === i ? { ...l, ...patch } : l)) });

  async function save() {
    setBusy(true);
    try {
      const ref = doc(collection(db, RECURRING_VOUCHER_COLLECTION));
      const total = (f.lines ?? []).reduce((n, l) => n + (Number(l.amount) || 0), 0);
      await setDoc(ref, {
        ...f,
        id: ref.id,
        total,
        narration: f.narration || f.name || '',
        paymentRef: f.paymentRef || '',
        active: true,
        createdAt: new Date().toISOString(),
        createdBy: actor.uid,
      } as RecurringVoucher);
      onClose();
    } catch (e) {
      onError((e as Error).message);
    }
    setBusy(false);
  }

  return (
    <div className={modalBackdrop} onClick={onClose}>
      <div className={modalSurface('max-w-2xl w-full')} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-gray-900 dark:text-white">New standing bill</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3 max-h-[65vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">What is it</span>
              <input value={f.name ?? ''} onChange={(e) => setF({ ...f, name: e.target.value })}
                className={glassFieldClass('mt-1')} placeholder="Security contract" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Paid to</span>
              <select
                value={f.supplierId ?? ''}
                onChange={(e) => {
                  const s = suppliers.find((x) => x.id === e.target.value);
                  setF({ ...f, supplierId: s?.id, payeeName: s?.name ?? f.payeeName });
                }}
                className={glassFieldClass('mt-1')}>
                <option value="">— not in the register —</option>
                {suppliers.filter((s) => s.active !== false).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {!f.supplierId && (
                <input value={f.payeeName ?? ''}
                  onChange={(e) => setF({ ...f, payeeName: e.target.value })}
                  className={glassFieldClass('mt-1')} placeholder="Who is paid" />
              )}
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Starts</span>
              <input type="month" value={f.startPeriod ?? ''}
                onChange={(e) => setF({ ...f, startPeriod: e.target.value })}
                className={glassFieldClass('mt-1')} />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">
                Ends (optional)
              </span>
              <input type="month" value={f.endPeriod ?? ''}
                onChange={(e) => setF({ ...f, endPeriod: e.target.value || undefined })}
                className={glassFieldClass('mt-1')} />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">
                Due day
              </span>
              <input type="number" min={1} max={28} value={f.dueDay ?? 1}
                onChange={(e) => setF({ ...f, dueDay: Number(e.target.value) })}
                className={glassFieldClass('mt-1 text-right')} />
            </label>
          </div>

          {(f.lines ?? []).map((l, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input value={l.description}
                onChange={(e) => setLine(i, { description: e.target.value })}
                className={glassFieldClass()} placeholder="What the charge is" />
              <select value={l.accountCode}
                onChange={(e) => setLine(i, { accountCode: e.target.value })}
                className={glassFieldClass()}>
                <option value="">— account —</option>
                {expenses.map((a) => (
                  <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
                ))}
              </select>
              <input type="number" step="0.01" value={l.amount || ''}
                onChange={(e) => setLine(i, { amount: Number(e.target.value) })}
                className={glassFieldClass('text-right')} placeholder="Amount" />
            </div>
          ))}
          <button type="button" className={btnGhost}
            onClick={() => setF({ ...f, lines: [...(f.lines ?? []), { description: '', accountCode: '', amount: 0 }] })}>
            <Plus size={14} /> Add a line
          </button>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">
              Reference the payee sees
            </span>
            <input value={f.paymentRef ?? ''} onChange={(e) => setF({ ...f, paymentRef: e.target.value })}
              className={glassFieldClass('mt-1')} />
          </label>

          {/* Said where somebody about to press the button will read it. */}
          <p className="text-[12px] text-gray-500">
            This never pays anything. Each period it produces a DRAFT voucher, which still has
            to be checked and approved by different people — the same three signatures as any
            other payment.
          </p>
          {problem && <p className="text-[13px] text-amber-700 dark:text-amber-300">{problem}</p>}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-100 dark:border-gray-700">
          <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} onClick={save}
            disabled={busy || Boolean(problem)}>
            {busy ? 'Saving…' : 'Save it'}
          </button>
        </div>
      </div>
    </div>
  );
}
